import type { Map as MapType } from "mapbox-gl";
import moment from "moment/min/moment-with-locales";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Accordion,
  Icon,
  ListItem,
  Markdown,
  ModalButton,
  Tabs,
  useIsMobile,
} from "senf-atomic-design-system";
import {
  capitalizeFirstLetter,
  fixFalslyTranslatedMarkdown,
  formatBytes,
  openLink,
  throttle,
  useGlobalState,
} from "senf-shared";
import type {
  Project,
  SectionButton,
  SectionCalendar,
  SectionFAQ,
  SectionFile,
  SectionImage,
  SectionMap,
  SectionText,
  SectionTimeline,
} from "senf-shared/types";
import { cn } from "../../../util/classNameConstructor";
import MapViewEditor from "../../atoms/map/MapViewEditor";
import Button from "../../core/button/Button";
import Pagination from "../../core/pagination/Pagination";
import TruncateMiddle from "../../core/TruncateMiddle";
import useTable from "../../organisms/table/useTable";
import { CopyrightIcon } from "../copyright/CopyrightIcon";
import SortableLogos from "../survey/components/SortableLogos";
import { EventItem, EventThumbnail } from "./InfoPageBuilderComponents";

interface SectionProps<T> {
  section: T;
  project?: Project;
  isMobile?: boolean;
}

interface ImageData {
  url: string;
  alt?: string;
  copyright?: string;
  height?: number;
  width?: number;
  keepAspectRatio?: boolean;
}

export const TextSection = ({
  section,
  as: Component = "section",
}: SectionProps<SectionText> & { as?: keyof JSX.IntrinsicElements }) => {
  const isMobile = useIsMobile();
  const [translation] = useGlobalState().translationsState;

  return (
    <Component
      className={cn(
        "flex flex-col gap-3 pointer-events-auto z-10",
        // Add white background on mobile for standalone sections
        Component === "section" && "bg-white",
        Component === "section" && (isMobile ? "px-5 py-8" : "px-8 py-10"),
        // isMobile ? "px-5 py-8" : "px-8 py-10",
      )}
      section-id={section.uid}
      {...(Component === "section" && {
        "aria-label": translation?.[section?.title] || section?.title,
      })} // Only add aria-label if it's a section
    >
      <h3 className="heading-2xl">
        {translation?.[section?.title] || section?.title}
      </h3>
      <p className="body-md">
        <Markdown>
          {fixFalslyTranslatedMarkdown(
            (section.body && translation?.[section.body]) || section.body,
          )}
        </Markdown>
      </p>
    </Component>
  );
};

export const MapSection = ({ section, isMobile }: SectionProps<SectionMap>) => {
  const { t } = useTranslation();
  const [translation] = useGlobalState().translationsState;
  const sectionRef = useRef<HTMLElement>(null);

  // Individual animation state for this MapSection
  const [windowState, setWindowState] = useState({
    scale: 1,
    isFullscreen: false,
  });

  // Pointer events lock state
  const [isMapLocked, setIsMapLocked] = useState(true); // Start locked (no map interaction)

  // Track if user has tried to interact with the map
  const [hasTriedToInteract, setHasTriedToInteract] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const hideTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Handle map interaction attempts
  const handleMapInteractionAttempt = useCallback(() => {
    if (isMapLocked && !hasTriedToInteract) {
      setHasTriedToInteract(true);

      // Small delay to allow transition to work
      setTimeout(() => {
        setShowOverlay(true);
      }, 10);

      // Clear any existing timer
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }

      // Set timer to hide overlay after 3 seconds
      hideTimerRef.current = setTimeout(() => {
        setShowOverlay(false);
        // Hide after transition completes
        setTimeout(() => {
          setHasTriedToInteract(false);
        }, 300);
      }, 3000);
    }
  }, [isMapLocked, hasTriedToInteract]);

  // Auto-hide overlay after 3 seconds when it's shown
  useEffect(() => {
    if (isMapLocked && hasTriedToInteract && showOverlay) {
      // Clear any existing timer
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }

      // Set timer to hide overlay after 3 seconds
      hideTimerRef.current = setTimeout(() => {
        setShowOverlay(false);
        // Hide after transition completes
        setTimeout(() => {
          setHasTriedToInteract(false);
        }, 300);
      }, 3000);
    }
  }, [isMapLocked, hasTriedToInteract, showOverlay]);

  // Optimized scroll handler with throttling
  const handleScroll = useCallback(() => {
    if (!sectionRef.current) return;

    const rect = sectionRef.current.getBoundingClientRect();
    const windowHeight = window.innerHeight;

    // Calculate the bottom of the window frame (section bottom)
    const frameBottom = rect.bottom;

    let scale = 1;
    let isFullscreen = false;

    // Start scaling when the bottom frame hits the bottom of the screen
    if (frameBottom <= windowHeight) {
      // Calculate how much the frame has moved up from the bottom edge
      const scrollDistance = windowHeight - frameBottom;
      const maxScrollDistance = windowHeight; // Scale over full viewport height

      // Calculate progress (0 = just hit bottom, 1 = fully scrolled up)
      const progress = Math.min(
        1,
        Math.max(0, scrollDistance / maxScrollDistance),
      );

      // Scale up based on progress
      scale = 1 + progress * 1.5; // Scale from 1 to 2.5
      isFullscreen = progress > 0.8; // Go fullscreen when mostly scaled
    }

    setWindowState({ scale, isFullscreen });

    // Auto-lock map when scrolling away from the section
    if (!isMapLocked) {
      const sectionTop = rect.top;
      const sectionBottom = rect.bottom;

      // Lock if section is not visible (scrolled past or before)
      if (sectionBottom < 0 || sectionTop > windowHeight) {
        setIsMapLocked(true);
        setHasTriedToInteract(false);
        setShowOverlay(false);
        // Clear any existing timer
        if (hideTimerRef.current) {
          clearTimeout(hideTimerRef.current);
        }
      }
    }
  }, [isMapLocked]);

  // Throttled version for better performance
  const throttledHandleScroll = useMemo(
    () => throttle(handleScroll, 16), // ~60fps
    [handleScroll],
  );

  useEffect(() => {
    // Only add scroll animation on mobile
    if (!isMobile) {
      // Set default values for desktop
      setWindowState({ scale: 1, isFullscreen: false });
      return;
    }

    const scrollContainer = document.querySelector("[data-infopage-scroll]");
    scrollContainer?.addEventListener("scroll", throttledHandleScroll);
    window.addEventListener("resize", throttledHandleScroll);

    // Add event listeners to detect map interaction attempts
    const mapSection = sectionRef.current;
    if (mapSection) {
      const interactionEvents = ["touchstart", "mousedown", "pointerdown"];
      interactionEvents.forEach((eventType) => {
        mapSection.addEventListener(eventType, handleMapInteractionAttempt, {
          passive: true,
        });
      });
    }

    // Initial calculation
    handleScroll();

    return () => {
      scrollContainer?.removeEventListener("scroll", throttledHandleScroll);
      window.removeEventListener("resize", throttledHandleScroll);

      // Remove interaction event listeners
      if (mapSection) {
        const interactionEvents = ["touchstart", "mousedown", "pointerdown"];
        interactionEvents.forEach((eventType) => {
          mapSection.removeEventListener(
            eventType,
            handleMapInteractionAttempt,
          );
        });
      }

      // Clear timer on cleanup
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
    };
  }, [
    isMobile,
    throttledHandleScroll,
    handleScroll,
    handleMapInteractionAttempt,
  ]);

  return (
    <>
      <section
        ref={sectionRef}
        className={cn(
          "-z-10",
          isMapLocked && !hasTriedToInteract
            ? "pointer-events-auto"
            : "pointer-events-none",
          !isMobile && "mt-[15vh]",
        )}
        aria-label={translation?.[section.title] || section.title}
        section-id={section.uid}
      >
        {/* Map section with SVG clip-path window on mobile */}
        {isMobile ? (
          <>
            {/* White overlay container */}
            <div
              className={cn(
                "-mt-8 relative transition-all duration-300 ease-out -mx-12 -my-10",
                isMapLocked ? "pointer-events-auto" : "pointer-events-none",
              )}
              style={{
                // Apply scaling animation
                transform: `scale(${Math.min(windowState.scale, 1.5)})`,
                transformOrigin: "center 15%",
                opacity: windowState.scale > 1.5 ? 0 : 1,
                // pointerEvents: windowState.scale > 1.5 ? "none" : "auto",
                // Add smooth transitions
                transition: "transform 300ms ease-out",
              }}
            >
              {/* Middle section with left/right bars and transparent center */}
              <div className="flex w-full h-[90vh]">
                <div
                  className="flex-1 relative"
                  style={{
                    // Create visible white border with rounded corners
                    border: windowState.isFullscreen
                      ? "0px solid white"
                      : "70px solid white",
                    borderRadius: windowState.isFullscreen ? "0px" : "100px",
                    background: "transparent",
                    transition:
                      "border 300ms ease-out, border-radius 300ms ease-out",
                    // Control map interaction
                  }}
                />
              </div>
            </div>
          </>
        ) : null}
      </section>

      {isMobile && isMapLocked && hasTriedToInteract && (
        <>
          <div
            className={cn(
              "absolute inset-0 bg-black bg-opacity-50 -z-20 transition-opacity duration-700 ease-in-out",
              showOverlay ? "opacity-100" : "opacity-0",
            )}
          />
          <div
            className={cn(
              "flex items-center justify-center z-20 pointer-events-none transition-opacity duration-300 ease-in-out",
              showOverlay ? "opacity-100" : "opacity-0",
            )}
          >
            <Button
              leadingIcon="mousehand"
              text={t("util:unlock", { val: t("global:map") })}
              variant="primary"
              className={cn(
                "bg-white text-black pointer-events-auto transition-all duration-300 ease-in-out transform -mt-[35vh]",
                showOverlay ? "scale-100" : "scale-95",
              )}
              onClick={() => {
                setIsMapLocked(false);
                setShowOverlay(false);
                // Clear timer when user interacts with button
                if (hideTimerRef.current) {
                  clearTimeout(hideTimerRef.current);
                }
              }}
            />
          </div>
        </>
      )}

      {isMobile && (
        <div className="w-full h-8 bg-white rounded-t-3xl sticky -bottom-[22px] translate-y-[10px]">
          <Button
            leadingIcon="lock"
            variant="outline"
            text={t("util:lock", { val: t("global:map") })}
            className={cn(
              "bg-white",
              isMapLocked
                ? "opacity-0 pointer-events-none"
                : "opacity-100 pointer-events-auto",
            )}
            classNameWrapper="absolute -top-12 right-5 left-0 flex justify-end"
            onClick={() => {
              setIsMapLocked(true);
              // Reset interaction state when locking the map
              setHasTriedToInteract(false);
              setShowOverlay(false);
              // Clear any existing timer
              if (hideTimerRef.current) {
                clearTimeout(hideTimerRef.current);
              }
            }}
          />
        </div>
      )}
    </>
  );
};

export const LogoSection = ({ section }: SectionProps<SectionImage>) => {
  const isMobile = useIsMobile();

  return (
    <section
      section-id={section.uid}
      className={cn(
        "bg-white pointer-events-auto z-10",
        isMobile ? "px-5 py-5" : "px-8 py-10",
      )}
    >
      <SortableLogos mode="display" logos={section?.images} />
    </section>
  );
};

export const ImageSection = ({ section }: SectionProps<SectionImage>) => {
  const { t } = useTranslation();
  const [translation] = useGlobalState().translationsState;
  const isMobile = useIsMobile();

  return (
    <section
      className={cn(
        "grid gap-5 bg-white pointer-events-auto z-10",
        isMobile ? "px-5 py-8" : "px-8 py-10",
      )}
      aria-label={t("global:images")}
      section-id={section.uid}
    >
      {section?.images?.map((image: ImageData) => {
        const { url, alt, copyright } = image;
        const isVideo = section.type === "video";

        return (
          <div className="relative" key={url}>
            {copyright && (
              <CopyrightIcon
                copyright={copyright}
                captionId={`figcaption-${url}`}
                className={cn(isVideo && "top-0 bottom-auto")}
              />
            )}

            {!isVideo && (
              <img
                src={url}
                alt={(alt && translation?.[alt]) || alt || t("global:image")}
                className={cn("rounded-xl")}
                aria-describedby={`figcaption-${url}`}
              />
            )}
            {isVideo && (
              <div className="relative">
                <video
                  className={cn("mx-auto rounded-xl bg-black w-full")}
                  controls
                  playsInline
                  webkit-playsinline="true"
                  x5-playsinline="true"
                  preload="auto"
                  aria-describedby={`figcaption-${url}`}
                >
                  <source src={url} type="video/mp4" />
                  <source src={url} type="video/quicktime" />
                  <track kind="captions" />
                  Your browser does not support the video tag.
                </video>
                {alt && (
                  <span className="sr-only">
                    {translation?.[alt] || alt || t("global:decorativeVideo")}
                  </span>
                )}
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
};

export const FileSection = ({ section }: SectionProps<SectionFile>) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  if (!section.files?.length) return null;
  return (
    <div className={cn("z-10", isMobile ? "px-5 py-8" : "px-8 py-10")}>
      <section
        className="border-border-default flex flex-col rounded-2xl border p-1 pointer-events-auto"
        aria-label={t("global:files")}
        section-id={section.uid}
      >
        {[...(section.files || [])].map((file, i) => {
          return (
            <ListItem
              key={file.name}
              index={i}
              primaryContent={<TruncateMiddle text={file.name} />}
              secondaryContent={formatBytes(file.size, { decimals: 1 })}
              leadingIcon="file/pdf-28"
              onClick={(e) => {
                e.preventDefault();
                window.open(file?.blobUrl || file?.url, "_blank");
              }}
            />
          );
        })}
      </section>
    </div>
  );
};

export const FAQSection = ({ section }: SectionProps<SectionFAQ>) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [translation] = useGlobalState().translationsState;

  if (!section?.faq) return null;
  return (
    <section
      className={cn(
        "flex flex-col gap-5 bg-white pointer-events-auto z-10",
        isMobile ? "px-5 py-8" : "px-8 py-10",
      )}
      aria-label={translation?.[section.title] || section.title}
      section-id={section.uid}
    >
      <TextSection
        as="div"
        section={{
          ...section,
          title:
            section.title === null || section.title === undefined
              ? t("infoPage:faq.title")
              : translation?.[section.title] || section.title,
          body:
            section.body === null || section.body === undefined
              ? t("infoPage:faq.description")
              : translation?.[section.body] || section.body,
        }}
      />
      <Accordion
        data={section?.faq?.map((e) => ({
          stackTitle: translation?.[e.question] || e.question,
          stackContent: translation?.[e.answer] || e.answer,
        }))}
      />
    </section>
  );
};

export const ButtonSection = ({ section }: SectionProps<SectionButton>) => {
  const { t } = useTranslation();

  const isMobile = useIsMobile();
  const [translation] = useGlobalState().translationsState;

  return (
    <section
      className={cn(
        "flex justify-start gap-5 pointer-events-auto bg-white py-2",
        isMobile ? "px-5" : "px-8",
      )}
      aria-label={t("global:links")}
      section-id={section.uid}
    >
      {section?.buttons?.map(({ text, link, uid }) => {
        return (
          <Button
            text={translation?.[text] || text || link}
            key={uid}
            leadingIcon={link?.includes("@") ? "mail" : "link"}
            size="lg"
            variant="outline"
            onClick={() => {
              const formattedLink = link?.includes("@")
                ? `mailto:${link}`
                : link;
              openLink(formattedLink);
            }}
          />
        );
      })}
    </section>
  );
};

const EventDetails = ({ event }) => {
  const { t } = useTranslation();
  const [translation] = useGlobalState().translationsState;

  const startTime = moment(event.start.date).format("HH:mm");
  const endTime = moment(event.end.date).format("HH:mm");

  return (
    <section className="@container min-h-[320px]">
      <div className="items-between @lg:flex-row @lg:p-10 container flex flex-col justify-between gap-x-10 gap-y-5 p-5">
        <EventThumbnail event={event} />
        <div className="flex w-full flex-col gap-10">
          <div className="flex flex-col items-start gap-2">
            <span className="heading-3xl">
              {translation?.[event.summary] || event.summary}
            </span>
            <span className="body-md whitespace-pre-wrap">
              {translation?.[event.description] || event.description}
            </span>
            {event.url && (
              <Button
                variant="primary"
                leadingIcon={event.url.includes("mailto:") ? "mail" : undefined}
                trailingIcon={
                  event.url.includes("mailto:") ? undefined : "arrow-right-md"
                }
                text={
                  translation?.[event.xProps?.urlLabel] ||
                  event.xProps?.urlLabel
                }
                onClick={() => openLink(event.url)}
                classNameWrapper={
                  document.dir === "rtl" ? "ml-auto" : "mr-auto"
                }
              />
            )}
          </div>

          <div className="grid gap-5">
            <div className="bg-background-default grid grid-cols-2 gap-10 rounded-lg p-3 px-4">
              <div className="flex flex-col gap-1">
                <span className="body-md-bold">{t("global:date")}</span>
                <div className="flex items-start gap-1">
                  <Icon
                    name="calendar"
                    className="text-icon-default shrink-0"
                  />
                  <span className="">
                    {moment(event.start.date).format("DD. MMMM YYYY")}
                  </span>
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <span className="body-md-bold">{t("global:clockTime")}</span>
                <div className="flex items-start gap-1">
                  <Icon name="clock" className="text-icon-default shrink-0" />
                  <span className="">
                    {startTime === endTime
                      ? t("global:allDay")
                      : `${startTime} – ${endTime}`}
                  </span>
                </div>
              </div>
            </div>
            <div className="bg-background-default flex flex-col gap-1 rounded-lg p-3 px-4">
              <span className="body-md-bold">{t("global:eventLocation")}</span>
              <div className="flex flex-wrap items-center gap-3">
                {event.location && (
                  <span className="flex items-start gap-1">
                    <Icon name="pin" className="text-icon-default shrink-0" />
                    {event.location?.split(",").slice(0, 2).join(",")}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const CalendarSection = ({
  section,
  project,
}: SectionProps<SectionCalendar>) => {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const [translation] = useGlobalState().translationsState;

  moment.locale(i18n.resolvedLanguage);
  const [events] = useState([]); // Entfernen Sie setEvents, da es nicht verwendet wird

  const allEvents = useMemo(() => {
    return [...(section?.events || []).filter((e) => e), ...events].sort(
      (a, b) =>
        new Date(a.start.date).getTime() - new Date(b.start.date).getTime(),
    );
  }, [section?.events, events]);

  // Separate current and past events
  const currentEvents = useMemo(
    () => allEvents.filter((event) => new Date(event.start.date) >= new Date()),
    [allEvents],
  );

  const pastEvents = useMemo(
    () => allEvents.filter((event) => new Date(event.start.date) < new Date()),
    [allEvents],
  );

  const [activeTab, setActiveTab] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  const displayedEvents = useMemo(
    () =>
      currentEvents.length > 0 && pastEvents.length > 0
        ? activeTab === 1
          ? currentEvents
          : pastEvents
        : pastEvents.length > 0
          ? pastEvents
          : currentEvents,
    [currentEvents, pastEvents, activeTab],
  );

  const { slice, range } = useTable(displayedEvents, currentPage, 5);

  return (
    <>
      <section
        className={cn(
          "flex flex-col gap-2 bg-white pointer-events-auto z-10",
          isMobile ? "px-5 py-8" : "px-8 py-10",
        )}
        aria-label={t("global:calendar")}
        section-id={section.uid}
      >
        <TextSection
          as="div"
          section={{
            ...section,
            title:
              section.title === null || section.title === undefined
                ? t("global:calendar")
                : translation?.[section.title] || section.title,
          }}
        />

        {/* Show Tabs only if there are events in both currentEvents and pastEvents */}
        {currentEvents.length > 0 && pastEvents.length > 0 && (
          <Tabs
            active={activeTab}
            setActive={setActiveTab}
            classNames={{ tab: "heading-lg" }}
            tabs={[
              { text: t("global:upcoming") }, // Tab for current events
              { text: t("global:past") }, // Tab for past events
            ]}
            withBorder
          />
        )}

        <div className="flex gap-10">
          <div className="-mt-2 flex w-full flex-col">
            {slice?.map((event, i) => {
              return (
                <div className="flex flex-col" key={i}>
                  {((slice[i - 1] &&
                    moment(slice[i - 1].start.date).format("YYYY-MM") !==
                      moment(event.start.date).format("YYYY-MM")) ||
                    i === 0) && (
                    <span className="heading-lg text-text-subdued mt-5 normal-case">
                      {capitalizeFirstLetter(
                        moment(event.start.date).format("MMMM YYYY"),
                      )}
                    </span>
                  )}
                  <ModalButton
                    key={event.uid}
                    button={EventItem}
                    event={event}
                    options={{ id: event.uid, size: "lg", zIndex: 999999 }}
                    className="cursor-pointer"
                  >
                    <EventDetails event={event} />
                  </ModalButton>
                </div>
              );
            })}
          </div>
        </div>
        {range?.length > 1 && (
          <Pagination
            range={range}
            slice={slice}
            setPage={setCurrentPage}
            page={currentPage}
            showPages={10}
          />
        )}
      </section>
    </>
  );
};

export const TimelineSection = ({ section }: SectionProps<SectionTimeline>) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [translation] = useGlobalState().translationsState;

  if (!section?.timeline) return null;
  return (
    <section
      className={cn(
        "flex flex-col gap-5 bg-white pointer-events-auto z-10",
        isMobile ? "px-5 py-8" : "px-8 py-10",
      )}
      aria-label={translation?.[section.title] || section.title}
      section-id={section.uid}
    >
      <TextSection
        as="div"
        section={{
          ...section,
          title:
            section.title === null || section.title === undefined
              ? t("global:timeline")
              : translation?.[section.title] || section.title,
          body:
            section.body === null || section.body === undefined
              ? t("infoPage:timeline.description")
              : translation?.[section.body] || section.body,
        }}
      />

      <div className="flex flex-col gap-5">
        {section.timeline.map((item, index) => (
          <div key={item.uid} className="flex gap-5">
            <div className="relative flex flex-col items-center top-1.5">
              <div className="bg-surface-highlight-default h-3 w-3 rounded-full" />
              {index < section.timeline.length - 1 && (
                <div className="bg-border-subdued absolute top-4 h-full w-0.5" />
              )}
            </div>
            <div className="flex flex-col gap-2 pb-10">
              <span className="heading-xl">
                {translation?.[item.title] || item.title}
              </span>
              <span className="body-md whitespace-pre-wrap">
                <Markdown>
                  {fixFalslyTranslatedMarkdown(
                    translation?.[item.description] || item.description,
                  )}
                </Markdown>
              </span>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
